import type { Row } from "@rocicorp/zero";
import { type GrantLike, grantHasPermission } from "../../shared/rbac/roles";
import { userDisplayName } from "./userDisplayName";

export type ReviewerUser = Pick<
  Row["user"],
  "id" | "name" | "email" | "image" | "customAvatarKey" | "deactivated"
>;

type MemberGrant = GrantLike & {
  user?: ReviewerUser | null;
  userGroup?: {
    roleGrantsAsScope: ReadonlyArray<{ user?: ReviewerUser | null }>;
  } | null;
};

const SUGGESTION_WINDOW_MS = 90 * 24 * 60 * 60 * 1000;
const MAX_SUGGESTIONS = 3;

// Space members who could approve, with recent live pushers of the workflow
// first. Small spaces default to everyone so a request there still reaches all
// approvers without picking.
export function pushRequestReviewers(opts: {
  spaceGrants: readonly MemberGrant[];
  mainCommits: ReadonlyArray<{
    createdByUserId: string | null;
    createdAt: number;
  }>;
  excludedUserIds: ReadonlySet<string>;
  now: number;
}): {
  reviewers: ReviewerUser[];
  suggestedIds: ReadonlySet<string>;
  defaultIds: string[];
} {
  const eligible = new Map<string, ReviewerUser>();
  for (const grant of opts.spaceGrants) {
    if (!grantHasPermission(grant, "space:push_live")) continue;
    const users = grant.user
      ? [grant.user]
      : (grant.userGroup?.roleGrantsAsScope ?? []).flatMap((m) =>
          m.user ? [m.user] : []
        );
    for (const u of users) {
      if (!u.deactivated && !opts.excludedUserIds.has(u.id))
        eligible.set(u.id, u);
    }
  }

  const suggested = [
    ...new Set(
      opts.mainCommits
        .filter((c) => c.createdAt > opts.now - SUGGESTION_WINDOW_MS)
        .toSorted((a, b) => b.createdAt - a.createdAt)
        .flatMap((c) =>
          c.createdByUserId && eligible.has(c.createdByUserId)
            ? [c.createdByUserId]
            : []
        )
    ),
  ].slice(0, MAX_SUGGESTIONS);
  const suggestedIds = new Set(suggested);
  const others = [...eligible.values()]
    .filter((u) => !suggestedIds.has(u.id))
    .toSorted((a, b) => userDisplayName(a).localeCompare(userDisplayName(b)));

  return {
    reviewers: [...suggested.map((id) => eligible.get(id)!), ...others],
    suggestedIds,
    defaultIds:
      eligible.size <= MAX_SUGGESTIONS ? [...eligible.keys()] : suggested,
  };
}
